import React, { useState } from 'react';
import { motion } from 'framer-motion';

const NarrativeAnalysis = ({ data }) => {
  const [activeTab, setActiveTab] = useState('overview');

  if (!data || !data.narrativeScore) return <div style={{ padding: 60, textAlign: 'center', color: '#94a3b8', background: 'rgba(17,24,39,0.5)', borderRadius: 12, border: '1px dashed rgba(99,102,241,0.3)' }}>Narrative Analysis hasn't been run for this domain yet. Click 'Refresh Analysis' above to generate insights.</div>;

  const getStrengthColor = (strength) => {
    const colors = { Dominant: '#10b981', Strong: '#22c55e', Moderate: '#eab308', Emerging: '#f97316', Weak: '#ef4444' };
    return colors[strength] || '#d1d5db';
  };

  const getSentimentColor = (sentiment) => {
    const colors = { positive: '#10b981', negative: '#ef4444', neutral: '#d1d5db', mixed: '#eab308' };
    return colors[sentiment] || '#d1d5db';
  };

  const getPriorityColor = (priority) => {
    const colors = { High: '#ef4444', Medium: '#f97316', Low: '#22c55e' };
    return colors[priority] || '#d1d5db';
  };

  const getStageColor = (stage) => {
    const colors = { Introduction: '#3b82f6', Rising: '#8b5cf6', Climax: '#ef4444', Resolution: '#10b981', Dormant: '#d1d5db' };
    return colors[stage] || '#d1d5db';
  };

  const getFrequencyWidth = (freq) => {
    const widths = { High: '90%', Medium: '60%', Low: '30%' };
    return widths[freq] || '50%';
  };

  const getAlignmentIcon = (alignment) => {
    if (alignment === 'Aligned') return '';
    if (alignment === 'Partially Aligned') return '';
    return '';
  };

  const getAlignmentColor = (alignment) => {
    const colors = { Aligned: '#10b981', 'Partially Aligned': '#eab308', Misaligned: '#ef4444' };
    return colors[alignment] || '#d1d5db';
  };

  // Radar chart for themes
  const renderThemesRadar = () => {
    const themes = data.themes || [];
    if (themes.length === 0) return null;
    const cx = 150, cy = 150, r = 110;
    const angleStep = (2 * Math.PI) / themes.length;
    const points = themes.map((t, i) => {
      const angle = i * angleStep - Math.PI / 2;
      const val = (t.prevalence / 100) * r;
      return { x: cx + val * Math.cos(angle), y: cy + val * Math.sin(angle) };
    });
    const polygon = points.map(p => `${p.x},${p.y}`).join(' ');
    const gridLevels = [0.25, 0.5, 0.75, 1];

    return (
      <svg viewBox="0 0 300 300" style={{ width: '100%', maxWidth: 320, margin: '0 auto', display: 'block' }}>
        {gridLevels.map((level, i) => (
          <polygon key={i} points={themes.map((_, j) => {
            const angle = j * angleStep - Math.PI / 2;
            const val = level * r;
            return `${cx + val * Math.cos(angle)},${cy + val * Math.sin(angle)}`;
          }).join(' ')} fill="none" stroke="#374151" strokeWidth="0.5" />
        ))}
        {themes.map((_, i) => {
          const angle = i * angleStep - Math.PI / 2;
          return <line key={i} x1={cx} y1={cy} x2={cx + r * Math.cos(angle)} y2={cy + r * Math.sin(angle)} stroke="#374151" strokeWidth="0.5" />;
        })}
        <polygon points={polygon} fill="rgba(99, 102, 241, 0.25)" stroke="#6366f1" strokeWidth="2" />
        {themes.map((t, i) => {
          const angle = i * angleStep - Math.PI / 2;
          const lx = cx + (r + 25) * Math.cos(angle);
          const ly = cy + (r + 25) * Math.sin(angle);
          return (
            <g key={i}>
              <circle cx={points[i].x} cy={points[i].y} r="4" fill="#6366f1" />
              <text x={lx} y={ly} textAnchor="middle" dominantBaseline="middle" fill="#d1d5db" fontSize="9" fontWeight="500">
                {t.name.length > 14 ? t.name.slice(0, 12) + '..' : t.name}
              </text>
            </g>
          );
        })}
      </svg>
    );
  };

  // Timeline bar chart
  const renderTimeline = () => {
    const timeline = data.narrativeTimeline || [];
    if (timeline.length === 0) return null;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {timeline.map((t, i) => (
          <div key={i}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
              <span style={{ color: '#d1d5db', fontSize: 16, fontWeight: 600 }}>{t.period}</span>
              <span style={{ color: '#d1d5db', fontSize: 15 }}>{t.strength}%</span>
            </div>
            <div style={{ background: '#1f2937', borderRadius: 6, height: 24, overflow: 'hidden', position: 'relative' }}>
              <motion.div initial={{ width: 0 }} animate={{ width: `${t.strength}%` }} transition={{ duration: 1, delay: i * 0.2 }}
                style={{ height: '100%', borderRadius: 6, background: `linear-gradient(90deg, #6366f1, #8b5cf6)` }} />
            </div>
            <p style={{ color: '#d1d5db', fontSize: 14, marginTop: 3 }}>{t.narrative}</p>
          </div>
        ))}
      </div>
    );
  };

  // Score gauge
  const renderScoreGauge = () => {
    const score = data.narrativeScore || 0;
    const circumference = 2 * Math.PI * 60;
    const offset = circumference - (score / 100) * circumference;
    const color = score >= 70 ? '#10b981' : score >= 40 ? '#eab308' : '#ef4444';
    return (
      <svg viewBox="0 0 150 150" style={{ width: 160, height: 160, display: 'block', margin: '0 auto' }}>
        <circle cx="75" cy="75" r="60" fill="none" stroke="#1f2937" strokeWidth="10" />
        <circle cx="75" cy="75" r="60" fill="none" stroke={color} strokeWidth="10"
          strokeDasharray={circumference} strokeDashoffset={offset}
          strokeLinecap="round" transform="rotate(-90, 75, 75)" style={{ transition: 'stroke-dashoffset 1.5s ease-out' }} />
        <text x="75" y="68" textAnchor="middle" fill="#fff" fontSize="28" fontWeight="bold">{score}</text>
        <text x="75" y="88" textAnchor="middle" fill="#d1d5db" fontSize="11">/ 100</text>
      </svg>
    );
  };

  // Key messages frequency chart
  const renderMessagesChart = () => {
    const messages = data.keyMessages || [];
    if (messages.length === 0) return null;
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {messages.map((m, i) => (
          <div key={i} style={{ background: '#111827', borderRadius: 8, padding: 12 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
              <span style={{ color: '#d1d5db', fontSize: 16, fontWeight: 500, flex: 1 }}>{m.message}</span>
              <span style={{ color: getAlignmentColor(m.alignment), fontSize: 20, marginLeft: 8 }}>{getAlignmentIcon(m.alignment)}</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ flex: 1, background: '#1f2937', borderRadius: 4, height: 6, overflow: 'hidden' }}>
                <motion.div initial={{ width: 0 }} animate={{ width: getFrequencyWidth(m.frequency) }} transition={{ duration: 0.8, delay: i * 0.1 }}
                  style={{ height: '100%', borderRadius: 4, background: m.frequency === 'High' ? '#6366f1' : m.frequency === 'Medium' ? '#8b5cf6' : '#a78bfa' }} />
              </div>
              <span style={{ color: '#d1d5db', fontSize: 14, minWidth: 50 }}>{m.frequency}</span>
            </div>
            <span style={{ color: '#d1d5db', fontSize: 14, marginTop: 4, display: 'block' }}>{m.source}</span>
          </div>
        ))}
      </div>
    );
  };

  const tabs = [
    { id: 'overview', label: 'Overview' },
    { id: 'themes', label: 'Themes & Messages' },
    { id: 'strategy', label: 'Gaps & Strategy' }
  ];

  return (
    <div style={{ color: '#fff' }}>
      {/* Sub-tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 24, flexWrap: 'wrap' }}>
        {tabs.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            style={{ padding: '8px 18px', borderRadius: 8, border: 'none', cursor: 'pointer', fontSize: 16, fontWeight: 600,
              background: activeTab === tab.id ? '#6366f1' : '#1f2937', color: activeTab === tab.id ? '#fff' : '#d1d5db',
              transition: 'all 0.2s' }}>
            {tab.label}
          </button>
        ))}
      </div>

      {/* OVERVIEW TAB */}
      {activeTab === 'overview' && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          {/* Score + Metrics Row */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 24 }}>
            <div style={{ background: '#111827', borderRadius: 12, padding: 20, textAlign: 'center' }}>
              <h4 style={{ color: '#d1d5db', fontSize: 15, marginBottom: 12, textTransform: 'uppercase', letterSpacing: 1 }}>Narrative Score</h4>
              {renderScoreGauge()}
              <div style={{ marginTop: 8 }}>
                <span style={{ color: getStrengthColor(data.narrativeStrength), fontWeight: 700, fontSize: 18 }}>{data.narrativeStrength}</span>
              </div>
            </div>
            <div style={{ background: '#111827', borderRadius: 12, padding: 20 }}>
              <h4 style={{ color: '#d1d5db', fontSize: 15, marginBottom: 16, textTransform: 'uppercase', letterSpacing: 1 }}>Narrative Metrics</h4>
              {[
                { label: 'Consistency', value: data.consistency },
                { label: 'Reach', value: data.reach },
                { label: 'Clarity', value: data.clarity }
              ].map((m, i) => (
                <div key={i} style={{ marginBottom: 14 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ color: '#d1d5db', fontSize: 16 }}>{m.label}</span>
                    <span style={{ color: '#6366f1', fontSize: 16, fontWeight: 600 }}>{m.value}%</span>
                  </div>
                  <div style={{ background: '#1f2937', borderRadius: 4, height: 8, overflow: 'hidden' }}>
                    <motion.div initial={{ width: 0 }} animate={{ width: `${m.value}%` }} transition={{ duration: 1, delay: i * 0.2 }}
                      style={{ height: '100%', borderRadius: 4, background: 'linear-gradient(90deg, #6366f1, #a78bfa)' }} />
                  </div>
                </div>
              ))}
            </div>
            <div style={{ background: '#111827', borderRadius: 12, padding: 20 }}>
              <h4 style={{ color: '#d1d5db', fontSize: 15, marginBottom: 12, textTransform: 'uppercase', letterSpacing: 1 }}>Overall Narrative</h4>
              <p style={{ color: '#d1d5db', fontSize: 16, lineHeight: 1.6 }}>{data.overallNarrative}</p>
              <div style={{ marginTop: 16, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ background: '#1f2937', color: '#6366f1', padding: '4px 10px', borderRadius: 12, fontSize: 14 }}>
                  {data.dataSources?.serpResults || 0} SERP results
                </span>
                <span style={{ background: '#1f2937', color: '#8b5cf6', padding: '4px 10px', borderRadius: 12, fontSize: 14 }}>
                  {data.dataSources?.relatedKeywords || 0} keywords
                </span>
              </div>
            </div>
          </div>

          {/* Timeline */}
          <div style={{ background: '#111827', borderRadius: 12, padding: 20, marginBottom: 24 }}>
            <h4 style={{ color: '#d1d5db', fontSize: 15, marginBottom: 16, textTransform: 'uppercase', letterSpacing: 1 }}>Narrative Timeline</h4>
            {renderTimeline()}
          </div>

          {/* Competitive Narratives */}
          <div style={{ background: '#111827', borderRadius: 12, padding: 20 }}>
            <h4 style={{ color: '#d1d5db', fontSize: 15, marginBottom: 16, textTransform: 'uppercase', letterSpacing: 1 }}>Competitive Narratives</h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 12 }}>
              {(data.competitiveNarratives || []).map((c, i) => (
                <motion.div key={i} initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ delay: i * 0.1 }}
                  style={{ background: '#0d1117', borderRadius: 10, padding: 16, border: '1px solid #1f2937' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <span style={{ color: '#fff', fontWeight: 600, fontSize: 16 }}>{c.competitor}</span>
                    <span style={{ background: '#1f2937', color: '#6366f1', padding: '2px 8px', borderRadius: 10, fontSize: 15, fontWeight: 600 }}>{c.strength}%</span>
                  </div>
                  <p style={{ color: '#d1d5db', fontSize: 15, marginBottom: 6 }}>{c.narrative}</p>
                  <p style={{ color: '#6366f1', fontSize: 14, fontStyle: 'italic' }}>{c.differentiator}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      )}

      {/* THEMES & MESSAGES TAB */}
      {activeTab === 'themes' && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 16, marginBottom: 24 }}>
            {/* Themes Radar */}
            <div style={{ background: '#111827', borderRadius: 12, padding: 20 }}>
              <h4 style={{ color: '#d1d5db', fontSize: 15, marginBottom: 12, textTransform: 'uppercase', letterSpacing: 1 }}>Theme Prevalence</h4>
              {renderThemesRadar()}
            </div>
            {/* Theme Cards */}
            <div style={{ background: '#111827', borderRadius: 12, padding: 20 }}>
              <h4 style={{ color: '#d1d5db', fontSize: 15, marginBottom: 12, textTransform: 'uppercase', letterSpacing: 1 }}>Theme Details</h4>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxHeight: 400, overflowY: 'auto' }}>
                {(data.themes || []).map((t, i) => (
                  <div key={i} style={{ background: '#0d1117', borderRadius: 8, padding: 12, borderLeft: `3px solid ${getSentimentColor(t.sentiment)}` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                      <span style={{ color: '#fff', fontWeight: 600, fontSize: 16 }}>{t.name}</span>
                      <span style={{ color: getSentimentColor(t.sentiment), fontSize: 14, textTransform: 'capitalize' }}>{t.sentiment}</span>
                    </div>
                    <p style={{ color: '#d1d5db', fontSize: 15, marginBottom: 6 }}>{t.description}</p>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <div style={{ flex: 1, background: '#1f2937', borderRadius: 3, height: 4, overflow: 'hidden' }}>
                        <div style={{ width: `${t.prevalence}%`, height: '100%', background: getSentimentColor(t.sentiment), borderRadius: 3 }} />
                      </div>
                      <span style={{ color: '#d1d5db', fontSize: 13 }}>{t.prevalence}%</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Key Messages */}
          <div style={{ background: '#111827', borderRadius: 12, padding: 20, marginBottom: 24 }}>
            <h4 style={{ color: '#d1d5db', fontSize: 15, marginBottom: 16, textTransform: 'uppercase', letterSpacing: 1 }}>Key Messages</h4>
            {renderMessagesChart()}
          </div>

          {/* Story Arcs */}
          <div style={{ background: '#111827', borderRadius: 12, padding: 20 }}>
            <h4 style={{ color: '#d1d5db', fontSize: 15, marginBottom: 16, textTransform: 'uppercase', letterSpacing: 1 }}>Story Arcs</h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 12 }}>
              {(data.storyArcs || []).map((arc, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}
                  style={{ background: '#0d1117', borderRadius: 10, padding: 16, border: '1px solid #1f2937' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <span style={{ color: '#fff', fontWeight: 600, fontSize: 16 }}>{arc.arc}</span>
                    <span style={{ background: getStageColor(arc.stage) + '22', color: getStageColor(arc.stage), padding: '3px 10px', borderRadius: 10, fontSize: 14, fontWeight: 600 }}>
                      {arc.stage}
                    </span>
                  </div>
                  <p style={{ color: '#d1d5db', fontSize: 15, marginBottom: 8 }}>{arc.description}</p>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                    <span style={{ color: '#d1d5db', fontSize: 14 }}>Potential:</span>
                    <span style={{ color: getPriorityColor(arc.potential === 'High' ? 'Low' : arc.potential === 'Low' ? 'High' : 'Medium'), fontSize: 14, fontWeight: 600 }}>
                      {arc.potential}
                    </span>
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      )}

      {/* GAPS & STRATEGY TAB */}
      {activeTab === 'strategy' && (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}>
          {/* Narrative Gaps */}
          <div style={{ background: '#111827', borderRadius: 12, padding: 20, marginBottom: 24 }}>
            <h4 style={{ color: '#d1d5db', fontSize: 15, marginBottom: 16, textTransform: 'uppercase', letterSpacing: 1 }}>Narrative Gaps</h4>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12 }}>
              {(data.narrativeGaps || []).map((gap, i) => (
                <motion.div key={i} initial={{ opacity: 0, x: -10 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.1 }}
                  style={{ background: '#0d1117', borderRadius: 10, padding: 16, borderLeft: `3px solid ${getPriorityColor(gap.impact)}` }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                    <span style={{ color: '#fff', fontWeight: 600, fontSize: 16 }}>{gap.gap}</span>
                    <span style={{ background: getPriorityColor(gap.impact) + '22', color: getPriorityColor(gap.impact), padding: '2px 8px', borderRadius: 10, fontSize: 14, fontWeight: 600 }}>
                      {gap.impact} Impact
                    </span>
                  </div>
                  <p style={{ color: '#d1d5db', fontSize: 15 }}>{gap.recommendation}</p>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Recommendations */}
          <div style={{ background: '#111827', borderRadius: 12, padding: 20 }}>
            <h4 style={{ color: '#d1d5db', fontSize: 15, marginBottom: 16, textTransform: 'uppercase', letterSpacing: 1 }}>Strategic Recommendations</h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              {(data.recommendations || []).map((rec, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.1 }}
                  style={{ background: '#0d1117', borderRadius: 10, padding: 16, border: '1px solid #1f2937' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                    <span style={{ color: '#fff', fontWeight: 600, fontSize: 16 }}>{rec.title}</span>
                    <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                      <span style={{ background: getPriorityColor(rec.priority) + '22', color: getPriorityColor(rec.priority), padding: '2px 8px', borderRadius: 10, fontSize: 14, fontWeight: 600 }}>
                        {rec.priority}
                      </span>
                      <span style={{ background: '#1f2937', color: '#d1d5db', padding: '2px 8px', borderRadius: 10, fontSize: 13 }}>
                        {rec.effort} Effort
                      </span>
                    </div>
                  </div>
                  <p style={{ color: '#d1d5db', fontSize: 15, marginBottom: 8 }}>{rec.description}</p>
                  <p style={{ color: '#6366f1', fontSize: 14, fontStyle: 'italic' }}>Expected: {rec.expectedImpact}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default NarrativeAnalysis;
